import React, { useEffect, useState } from 'react';
import DefaultLayout from '../../layout/DefaultLayout';
import Loading from '../../components/Loading';
import Alert from '../../components/Alert';
import CustomPicker from '../../components/CustomPicker';
import creditoService from '../../services/creditoService';
import { useGlobalState } from '../../state';
import { useNavigate } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { toCurrency } from '../../utils/formaters';
import Breadcrumb from '../../components/Breadcrumbs/Breadcrumb';

type Linea = { id: number; nombre: string; cantidadDeCuotas: string };

const validationSchema = yup.object().shape({
    lineaCredito: yup.string().required('Debes elegir un tipo de préstamo'),
    monto: yup
        .number()
        .typeError('El monto debe ser un número')
        .required('El monto es obligatorio')
        .positive('El monto debe ser un número positivo'),
    cantidadCuotas: yup.string().required('Debes elegir las cuotas'),
});

const Simular: React.FC = () => {
    const navigate = useNavigate();
    const [user] = useGlobalState('user');
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | undefined>(undefined);

    const [lineas, setLineas] = useState<Linea[]>([]);
    const [lineasOptions, setLineasOptions] = useState<any[]>([]);
    const [selectedLinea, setSelectedLinea] = useState<string | undefined>();
    const [cuotasDisponibles, setCuotasDisponibles] = useState<number[]>([]);
    const [cuotasOptions, setCuotasOptions] = useState<any[]>([]);
    const [selectedCuota, setSelectedCuota] = useState<string | undefined>();
    const [maximos, setMaximos] = useState<any | undefined>(undefined);
    const [montoError, setMontoError] = useState<string | undefined>(undefined);
    const [fechaPrimeraCuota, setFechaPrimeraCuota] = useState<string>(() => {
        const today = new Date();
        const nextMonth = new Date(today);
        nextMonth.setMonth(today.getMonth() + 1);
        return nextMonth.toISOString().slice(0, 10); // YYYY-MM-DD
    });
    const [displayMonto, setDisplayMonto] = useState<string>('0,00');

    const todayIso = new Date().toISOString().slice(0, 10);

    const { control, handleSubmit, formState: { errors }, getValues, setValue, watch } = useForm({
        resolver: yupResolver(validationSchema),
        defaultValues: { lineaCredito: '', monto: 0, cantidadCuotas: '' }
    });

    useEffect(() => {
        const fetch = async () => {
            setLoading(true);
            setError(undefined);

            if (user === undefined) {
                return;
            }
            if (!user) {
                navigate('/auth/signin');
                return;
            }

            try {
                // Si el usuario es beneficiario (tiene datosUsuario.beneficiario) llamamos a la ruta de beneficiario,
                // en caso contrario (afiliado) usamos la ruta para afiliado.
                const isBeneficiario = Boolean(user?.datosUsuario?.beneficiario);
                const res: any = isBeneficiario
                    ? await creditoService.getLineasCreditoParaBenficiario?.()
                    : await creditoService.getLineasCreditoParaAfiliado?.();

                if (res?.success) {
                    const data: Linea[] = res.data ?? [];
                    setLineas(data);
                    setLineasOptions((data || []).map(l => ({ label: l.nombre, value: String(l.id) })));
                } else {
                    setError(res?.errors?.[0] ?? 'No se pudieron cargar las líneas de crédito');
                }
            } catch (ex: any) {
                setError(ex?.message ?? 'Ocurrió un error al cargar las líneas de crédito');
            } finally {
                setLoading(false);
            }
        };

        fetch();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user, navigate]);

    useEffect(() => {
        if (!selectedLinea) {
            setCuotasDisponibles([]);
            setCuotasOptions([]);
            setSelectedCuota(undefined);
            setMaximos(undefined);
            // No forzar validación aquí para evitar errores prematuros en el combo de cuotas
            setValue('cantidadCuotas', '', { shouldValidate: false, shouldDirty: false });
            return;
        }

        const linea = lineas.find(l => String(l.id) === String(selectedLinea));
        if (!linea) return;

        try {
            const cuotas = JSON.parse('[' + linea.cantidadDeCuotas + ']') as number[];
            cuotas.sort((a, b) => a - b);
            setCuotasDisponibles(cuotas);
            setCuotasOptions(cuotas.map(n => ({ label: String(n), value: String(n) })));
            setSelectedCuota(undefined);
            // Evitar validación inmediata al limpiar: el usuario debe poder elegir la cuota sin que YA aparezca el error
            setValue('cantidadCuotas', '', { shouldValidate: false, shouldDirty: false });
            setMaximos(undefined);
            setMontoError(undefined);
        } catch {
            setCuotasDisponibles([]);
            setCuotasOptions([]);
        }
    }, [selectedLinea, lineas, setValue]);

    useEffect(() => {
        const fetchMaximos = async () => {
            if (!user || !selectedLinea || !selectedCuota) return;
            setLoading(true);
            try {
                const res: any = await creditoService.calcularMaximos(
                    parseInt(selectedLinea),
                    parseInt(selectedCuota),
                    parseInt(user.datosUsuario.legajoPrevisional),
                    user.datosUsuario.beneficiario?.tipoBeneficiarioId
                );
                if (res?.success) {
                    setMaximos(res.data);
                } else {
                    setError(res?.errors?.[0] ?? 'No se pudieron calcular los máximos');
                }
            } catch (ex: any) {
                setError(ex?.message ?? 'Ocurrió un error al calcular máximos');
            } finally {
                setLoading(false);
            }
        };

        fetchMaximos();
    }, [selectedCuota, selectedLinea, user]);

    // Mantener display sincronizado cuando cambie el valor numérico del formulario (similar a Boleta)
    useEffect(() => {
        const monto = watch('monto');
        const numeric = typeof monto === 'number' && !isNaN(monto) ? monto : 0;
        const formatted = new Intl.NumberFormat('es-ES', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        }).format(numeric);
        setDisplayMonto(formatted);
    }, [watch]);

    const formatNumberEs = (n: number) =>
        new Intl.NumberFormat('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);

    const checkMonto = (override?: number) => {
        const montoIngresado = typeof override === 'number' ? override : Number(getValues('monto') ?? 0);
        if (!maximos) return;
        if (montoIngresado > (maximos.montoMaximoParaDestinatario ?? Number.POSITIVE_INFINITY)) {
            setMontoError('El monto no puede superar los ' + toCurrency(maximos.montoMaximoParaDestinatario));
        } else {
            setMontoError(undefined);
        }
    };

    const handleMontoChange = (raw: string) => {
        const digits = raw.replace(/\D/g, '');
        const centsInt = digits === '' ? 0 : parseInt(digits, 10);
        const numeric = centsInt / 100;

        setValue('monto', numeric, { shouldValidate: true, shouldDirty: true });
        setDisplayMonto("$" + formatNumberEs(numeric));
        checkMonto(numeric);
    };

    const handleMontoFocus = (e: React.FocusEvent<HTMLInputElement>) => {
        e.target.setSelectionRange(e.target.value.length, e.target.value.length);
    };

    const onSubmit = (data: any) => {
        const payload = {
            idLineaDeCredito: selectedLinea,
            idAfiliado: user?.legajoPrevisional,
            monto: data.monto,
            cantidadDeCuotas: selectedCuota,
            fechaPrimeraCuota: fechaPrimeraCuota,
            generarReporte: false
        };
        const encodedData = encodeURIComponent(JSON.stringify(payload));
        navigate(`/prestamos/resultado-simular?data=${encodedData}`);
    };

    return (
        <DefaultLayout>

            <Breadcrumb pageName="Solicitar un Préstamo" />

            <div className="p-2">
                <div className="max-w-4xl mx-auto">
                    <h1 className="text-2xl font-semibold mb-4">Simulación de Crédito</h1>

                    {loading && <Loading statusText="Cargando..." />}

                    {error && <Alert type="failure" message={error} />}

                    {!loading && (
                        <div className="bg-white dark:bg-boxdark p-6 rounded space-y-4">
                            {lineasOptions.length > 0 ? (
                                <>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Elije el tipo de préstamo</label>
                                        <Controller
                                            control={control}
                                            name="lineaCredito"
                                            render={({ field }) => (
                                                <CustomPicker
                                                    options={lineasOptions}
                                                    onValueChange={(v: string | number) => {
                                                        const value = String(v);
                                                        setSelectedLinea(value);
                                                        field.onChange(value); // actualizar react-hook-form
                                                    }}
                                                    // Preferir el valor controlado por react-hook-form si existe
                                                    selectedValue={field.value || selectedLinea}
                                                    placeholder="Seleccione una opción"
                                                />
                                            )}
                                        />
                                        {errors.lineaCredito && <p className="text-red-600 text-sm mt-1">{(errors.lineaCredito as any).message}</p>}
                                    </div>

                                    {cuotasOptions.length > 0 && (
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Elije la cantidad de cuotas</label>
                                            <Controller
                                                control={control}
                                                name="cantidadCuotas"
                                                render={({ field }) => (
                                                    <CustomPicker
                                                        options={cuotasOptions}
                                                        onValueChange={(v: string | number) => {
                                                            const value = String(v);
                                                            setSelectedCuota(value);
                                                            field.onChange(value); // actualizar react-hook-form
                                                        }}
                                                        // usar el valor del form cuando exista; fallback a selectedCuota para mantener compatibilidad
                                                        selectedValue={field.value || selectedCuota}
                                                        placeholder="Seleccione cuotas"
                                                    />
                                                )}
                                            />
                                            {errors.cantidadCuotas && <p className="text-red-600 text-sm mt-1">{(errors.cantidadCuotas as any).message}</p>}
                                        </div>
                                    )}

                                    {maximos && (
                                        <div className="bg-gray-50 dark:bg-gray-800 rounded p-4">
                                            <div className="text-sm text-primario-azul-400">
                                                Monto máximo disponible: <span className="font-bold">{toCurrency(maximos.montoMaximoParaDestinatario)}</span>
                                            </div>
                                            <div className="text-sm text-primario-azul-400 mt-2">
                                                Tu cuota no debe superar: <span className="font-bold">{toCurrency(maximos.montoMaximoCutotaParaDestinatario)}</span> mensuales.
                                            </div>
                                        </div>
                                    )}

                                    {maximos && (
                                        <>
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1 mt-4">¿Cuánto dinero necesitás?</label>
                                                <Controller
                                                    control={control}
                                                    name="monto"
                                                    render={() => (
                                                        <input
                                                            type="text"
                                                            inputMode="numeric"
                                                            value={displayMonto}
                                                            onChange={(e) => handleMontoChange(e.target.value)}
                                                            onFocus={handleMontoFocus}
                                                            className={`mt-1 block w-full rounded-md border text-3xl ${errors.monto ? 'border-red-500' : 'border-gray-300'} p-2`}
                                                            placeholder="0,00"
                                                        />
                                                    )}
                                                />
                                                {errors.monto && <p className="text-red-600 text-sm mt-1">{(errors.monto as any).message}</p>}
                                                {montoError && <p className="text-red-600 text-sm mt-1">{montoError}</p>}
                                            </div>

                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1 mt-4">¿Cuando quieres pagar la primera cuota?</label>
                                                <input
                                                    type="date"
                                                    value={fechaPrimeraCuota}
                                                    onChange={(e) => setFechaPrimeraCuota(e.target.value)}
                                                    min={todayIso}
                                                    className="mt-1 block rounded-md border border-gray-300 p-2"
                                                />
                                            </div>

                                            {selectedCuota && !montoError && (
                                                <div className="mt-6">
                                                    <button
                                                        onClick={handleSubmit(onSubmit)}
                                                        disabled={loading}
                                                        className={`px-6 py-2 rounded font-semibold text-white ${loading ? 'bg-gray-400' : 'bg-primario-celeste-800'}`}
                                                    >
                                                        {loading ? 'Procesando...' : 'Simular'}
                                                    </button>
                                                </div>
                                            )}
                                        </>
                                    )}
                                </>
                            ) : (
                                <div className="text-sm text-gray-600">Actualmente no existen líneas de crédito disponibles para vos. Vuelve a intentarlo más adelante.</div>
                            )}
                        </div>
                    )}
                </div>

                <div className="flex gap-3 justify-center mt-8">
                    <button
                        onClick={() => navigate(-1)}
                        className="inline-flex items-center gap-2 bg-white border border-gray-300 text-gray-700 px-4 py-2 rounded"
                    >
                        Volver
                    </button>
                </div>
            </div>
        </DefaultLayout>
    );
};

export default Simular;